// Registry of live query-tab editors, keyed by tabId.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § NAV1 (amended 2026-08-23).
//
// After a DB-object rename the post-apply pass must rewrite open tabs so that
// the edit lands on the tab's UNDO STACK — a store write would instead reach
// Monaco through `useQueryBufferSync`'s external-change effect, which calls
// `editor.setValue()` and throws the undo history away.
//
// Monaco models here carry a random `inmemory://sql-studio/<nonce>.sql` path
// (SqlMonaco assigns one per mount), so there is no URI that identifies a tab.
// Each mounted query tab therefore registers its editor handle by tabId, the
// same shape `chartRefRegistry` uses for notebook chart panels. Tabs with no
// entry (never mounted, Monaco chunk still loading) fall back to a plain
// workspace-store write, which is picked up when the tab mounts.

/** Structural subset of `SqlMonacoHandle` the rewrite needs. `executeEdits`
 *  brackets the splice with `pushUndoStop()` on both sides, so the rewrite is
 *  always its own undo unit and never coalesces into the user's typing. */
export type RenameBufferEditor = {
  /** Live model text. The rewrite plan's byte offsets were computed against the
   *  STORE copy of the buffer, which `useQueryBufferSync` only flushes on a
   *  200 ms debounce — and a concurrent writer (an AI agent, MCP's
   *  `ui_set_editor_text`) can move it further. Splicing stale offsets into a
   *  drifted model corrupts it, so the applier compares first and refuses on a
   *  mismatch. `undefined` means "cannot tell" (no live model yet) and skips
   *  the comparison. Optional so existing handle doubles keep type-checking; a
   *  handle that omits it is trusted (pre-existing behaviour). */
  readonly getValue?: () => string | undefined;
  readonly executeEdits?: (
    source: string,
    edits: ReadonlyArray<{
      readonly range: {
        readonly startLineNumber: number;
        readonly startColumn: number;
        readonly endLineNumber: number;
        readonly endColumn: number;
      };
      readonly text: string;
    }>,
  ) => boolean;
};

/** Undo-stop source tag — mirrors VQE's `vqe-edit`, so the origin of the
 *  splice is visible in Monaco's undo metadata. */
export const RENAME_TAB_UNDO_SOURCE = 'nav-rename-tab-rewrite';

const registry = new Map<string, RenameBufferEditor>();

export function registerRenameBufferEditor(
  tabId: string,
  editor: RenameBufferEditor,
): void {
  registry.set(tabId, editor);
}

export function unregisterRenameBufferEditor(tabId: string): void {
  registry.delete(tabId);
}

export function getRenameBufferEditor(tabId: string): RenameBufferEditor | null {
  return registry.get(tabId) ?? null;
}

/** Test-only: drop every registration. */
export function _resetRenameBufferEditorsForTests(): void {
  registry.clear();
}
